import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface EmailIssue {
  id: number;
  message_id?: string | null;
  thread_id?: string | null;
  source: string;
  from_email: string;
  from_name?: string | null;
  to_email?: string | null;
  subject?: string | null;
  snippet?: string | null;
  body?: string | null;
  received_at?: string | null;
  client_exists: boolean;
  project_history_id?: number | null;
  project_id?: string | null;
  user_id?: number | null;
  client_name?: string | null;
  client_phone?: string | null;
  classification: string;
  has_problem: boolean;
  problem_score: number;
  problem_keywords?: string[] | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface EmailIssueStats {
  total: number;
  problem_count: number;
  no_problem_count: number;
  client_known_count: number;
  client_unknown_count: number;
  new_count: number;
  in_progress_count: number;
  treated_count: number;
  ignored_count: number;
  last_received_at?: string | null;
}

export interface PaginatedEmailIssueResponse {
  items: EmailIssue[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface EmailIssueFilters {
  classification?: string | null;
  status?: string | null;
  client_exists?: boolean | null;
  user_id?: number | null;
  search?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class EmailIssuesService {
  private apiUrl = `${(environment as any).casApiUrl || environment.apiUrl}${environment.apiEndpoint}/email-issues`;

  constructor(private http: HttpClient) {}

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  getStats(userId?: number | null): Observable<EmailIssueStats> {
    let params = new HttpParams();
    if (userId !== null && userId !== undefined) {
      params = params.set('user_id', String(userId));
    }

    return this.http.get<EmailIssueStats>(`${this.apiUrl}/stats`, {
      headers: this.getAuthHeaders(),
      params
    });
  }

  getIssues(
    limit: number = 25,
    offset: number = 0,
    filters: EmailIssueFilters = {}
  ): Observable<PaginatedEmailIssueResponse> {
    let params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString());

    if (filters.classification) {
      params = params.set('classification', filters.classification);
    }

    if (filters.status) {
      params = params.set('status', filters.status);
    }

    if (filters.client_exists !== null && filters.client_exists !== undefined) {
      params = params.set('client_exists', String(filters.client_exists));
    }

    if (filters.user_id !== null && filters.user_id !== undefined) {
      params = params.set('user_id', String(filters.user_id));
    }

    if (filters.search?.trim()) {
      params = params.set('search', filters.search.trim());
    }

    return this.http.get<PaginatedEmailIssueResponse>(this.apiUrl, {
      headers: this.getAuthHeaders(),
      params
    });
  }

  updateStatus(issueId: number, status: string): Observable<EmailIssue> {
    return this.http.patch<EmailIssue>(
      `${this.apiUrl}/${issueId}/status`,
      { status },
      { headers: this.getAuthHeaders() }
    );
  }
}
